import React, { useState } from 'react';
import { BookOpen, Search, Filter, Lightbulb, Target, FileText, TrendingUp, CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';
import { t } from '../services/i18n';

interface GuideSection {
  id: string;
  title: string;
  category: 'ai-detection' | 'seo-basics' | 'keyword-research' | 'content-optimization';
  icon: React.ReactNode;
  content: {
    title: string;
    description?: string;
    items: string[];
    tips?: string[];
    examples?: { label: string; text: string }[];
  };
}

export const SEOGuide: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const categories = [
    { id: 'all', label: t('seoGuide.categories.all'), icon: <BookOpen size={18} /> },
    { id: 'ai-detection', label: t('seoGuide.categories.aiDetection'), icon: <AlertCircle size={18} /> },
    { id: 'seo-basics', label: t('seoGuide.categories.seoBasics'), icon: <Target size={18} /> },
    { id: 'keyword-research', label: t('seoGuide.categories.keywordResearch'), icon: <TrendingUp size={18} /> },
    { id: 'content-optimization', label: t('seoGuide.categories.contentOptimization'), icon: <FileText size={18} /> },
  ];

  // Define guide sections inside component so translations are loaded
  const guideSections: GuideSection[] = [
    {
      id: 'ai-detection-triggers',
      title: t('seoGuide.sections.aiDetection.title'),
      category: 'ai-detection',
      icon: <AlertCircle className="text-orange-400" size={24} />,
      content: {
        title: t('seoGuide.sections.aiDetection.title'),
        description: t('seoGuide.sections.aiDetection.description'),
        items: [
          t('seoGuide.sections.aiDetection.items.uniformStructure'),
          t('seoGuide.sections.aiDetection.items.repetitivePhrasing'),
          t('seoGuide.sections.aiDetection.items.lowPerplexity'),
          t('seoGuide.sections.aiDetection.items.lackOfAnecdotes'),
          t('seoGuide.sections.aiDetection.items.genericLanguage'),
          t('seoGuide.sections.aiDetection.items.roboticTone'),
          t('seoGuide.sections.aiDetection.items.overOptimization'),
          t('seoGuide.sections.aiDetection.items.lackOfBurstiness'),
        ],
        tips: [
          t('seoGuide.sections.aiDetection.tips.varySentences'),
          t('seoGuide.sections.aiDetection.tips.addPersonalStories'),
          t('seoGuide.sections.aiDetection.tips.useIdioms'),
          t('seoGuide.sections.aiDetection.tips.allowImperfections'),
        ],
      },
    },
    {
      id: 'e-e-a-t-principles',
      title: t('seoGuide.sections.eeat.title'),
      category: 'seo-basics',
      icon: <CheckCircle className="text-emerald-400" size={24} />,
      content: {
        title: t('seoGuide.sections.eeat.title'),
        description: t('seoGuide.sections.eeat.description'),
        items: [
          t('seoGuide.sections.eeat.items.experience'),
          t('seoGuide.sections.eeat.items.expertise'),
          t('seoGuide.sections.eeat.items.authoritativeness'),
          t('seoGuide.sections.eeat.items.trustworthiness'),
        ],
        examples: [
          { label: t('seoGuide.sections.eeat.examples.experience.label'), text: t('seoGuide.sections.eeat.examples.experience.text') },
          { label: t('seoGuide.sections.eeat.examples.expertise.label'), text: t('seoGuide.sections.eeat.examples.expertise.text') },
        ],
      },
    },
    {
      id: 'user-intent',
      title: t('seoGuide.sections.userIntent.title'),
      category: 'seo-basics',
      icon: <Target className="text-blue-400" size={24} />,
      content: {
        title: t('seoGuide.sections.userIntent.title'),
        description: t('seoGuide.sections.userIntent.description'),
        items: [
          t('seoGuide.sections.userIntent.items.answerDirectly'),
          t('seoGuide.sections.userIntent.items.rightTone'),
          t('seoGuide.sections.userIntent.items.personalize'),
        ],
        examples: [
          { label: t('seoGuide.sections.userIntent.examples.query.label'), text: t('seoGuide.sections.userIntent.examples.query.text') },
        ],
      },
    },
    {
      id: 'keyword-optimization',
      title: t('seoGuide.sections.keywordOptimization.title'),
      category: 'keyword-research',
      icon: <TrendingUp className="text-purple-400" size={24} />,
      content: {
        title: t('seoGuide.sections.keywordOptimization.title'),
        description: t('seoGuide.sections.keywordOptimization.description'),
        items: [
          t('seoGuide.sections.keywordOptimization.items.chooseFocus'),
          t('seoGuide.sections.keywordOptimization.items.placeInTitle'),
          t('seoGuide.sections.keywordOptimization.items.useSynonyms'),
          t('seoGuide.sections.keywordOptimization.items.monitorPerformance'),
        ],
        tips: [
          t('seoGuide.sections.keywordOptimization.tips.specificKeywords'),
          t('seoGuide.sections.keywordOptimization.tips.naturalPlacement'),
        ],
      },
    },
    {
      id: 'content-structure',
      title: t('seoGuide.sections.contentStructure.title'),
      category: 'content-optimization',
      icon: <FileText className="text-indigo-400" size={24} />,
      content: {
        title: t('seoGuide.sections.contentStructure.title'),
        description: t('seoGuide.sections.contentStructure.description'),
        items: [
          t('seoGuide.sections.contentStructure.items.clearHeadings'),
          t('seoGuide.sections.contentStructure.items.tableOfContents'),
          t('seoGuide.sections.contentStructure.items.shortParagraphs'),
          t('seoGuide.sections.contentStructure.items.multimedia'),
        ],
      },
    },
    {
      id: 'readability',
      title: t('seoGuide.sections.readability.title'),
      category: 'content-optimization',
      icon: <BookOpen className="text-cyan-400" size={24} />,
      content: {
        title: t('seoGuide.sections.readability.title'),
        description: t('seoGuide.sections.readability.description'),
        items: [
          t('seoGuide.sections.readability.items.shortSentences'),
          t('seoGuide.sections.readability.items.frequentSubheadings'),
          t('seoGuide.sections.readability.items.transitionWords'),
          t('seoGuide.sections.readability.items.simpleLanguage'),
        ],
        tips: [
          t('seoGuide.sections.readability.tips.bulletPoints'),
          t('seoGuide.sections.readability.tips.varyLength'),
        ],
      },
    },
  ];

  const filteredSections = guideSections.filter((section) => {
    const matchesCategory = selectedCategory === 'all' || section.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.content.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.content.items.some((item) => item.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="p-8 animate-fadeIn text-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Lightbulb className="text-yellow-400" size={32} />
            <h2 className="text-3xl font-bold">{t('seoGuide.title')}</h2>
          </div>
          <p className="text-slate-400 text-lg">{t('seoGuide.subtitle')}</p>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder={t('seoGuide.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {cat.icon}
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Guide Sections */}
        <div className="space-y-4">
          {filteredSections.length === 0 ? (
            <div className="text-center py-12 bg-slate-800 rounded-2xl border border-slate-700">
              <Info className="mx-auto text-slate-400 mb-4" size={48} />
              <p className="text-slate-400">{t('seoGuide.noResults')}</p>
            </div>
          ) : (
            filteredSections.map((section) => (
              <div
                key={section.id}
                className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden transition-all hover:border-indigo-500/50"
              >
                <button
                  onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                  className="w-full p-6 flex items-center justify-between text-left hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {section.icon}
                    <div>
                      <h3 className="text-xl font-bold text-white">{section.content.title}</h3>
                      {section.content.description && (
                        <p className="text-slate-400 text-sm mt-1">{section.content.description}</p>
                      )}
                    </div>
                  </div>
                  <div className={`transform transition-transform ${expandedSection === section.id ? 'rotate-180' : ''}`}>
                    <Filter size={20} className="text-slate-400" />
                  </div>
                </button>

                {expandedSection === section.id && (
                  <div className="px-6 pb-6 border-t border-slate-700 pt-6 space-y-6">
                    {/* Main Points */}
                    {section.content.items && section.content.items.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3 text-indigo-400">{t('seoGuide.keyPoints')}</h4>
                        <ul className="space-y-2">
                          {section.content.items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-slate-300">
                              <CheckCircle size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Tips */}
                    {section.content.tips && section.content.tips.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3 text-yellow-400">{t('seoGuide.tips')}</h4>
                        <ul className="space-y-2">
                          {section.content.tips.map((tip, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-slate-300">
                              <Lightbulb size={18} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Examples */}
                    {section.content.examples && section.content.examples.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3 text-blue-400">{t('seoGuide.examples')}</h4>
                        <div className="space-y-3">
                          {section.content.examples.map((example, idx) => (
                            <div key={idx} className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                              <div className="font-semibold text-white mb-2">{example.label}</div>
                              <div className="text-slate-300 italic">"{example.text}"</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Quick Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <div className="text-2xl font-bold text-indigo-400">{guideSections.length}</div>
            <div className="text-slate-400 text-sm">{t('seoGuide.stats.guides')}</div>
          </div>
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <div className="text-2xl font-bold text-emerald-400">{categories.length - 1}</div>
            <div className="text-slate-400 text-sm">{t('seoGuide.stats.categories')}</div>
          </div>
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <div className="text-2xl font-bold text-yellow-400">{t('seoGuide.stats.free')}</div>
            <div className="text-slate-400 text-sm">{t('seoGuide.stats.access')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

